import React, { useState } from 'react';
import { Header, Icon, Message, Segment, Table, Checkbox, Button, Form } from 'semantic-ui-react';
import type { ContractLineFormItem } from '../../types/contractForm';
import type { TeamContractRules } from '../../services/teams/teamService';
import { SuspensionBlockBanner } from '../clientCancellationLines/SuspensionBlockBanner';
import { CLIENT_CANCELLATION_TIM_1056_SCRIPT } from '../../utils/clientCancellationLines/clientCancellationLinesLabels';

export interface ContractFormLinhasDesativarTabProps {
  linhas: ContractLineFormItem[];
  setLinhas: React.Dispatch<React.SetStateAction<ContractLineFormItem[]>>;
  linhasParaDesativar: Set<string>;
  setLinhasParaDesativar: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleLinhaDesativar: (linhaId: string) => void;
  calcularDataDesativacao: () => string;
  teamContractRules: TeamContractRules;
  calcularAditivosNecessarios: (linhasSuspensas: number) => number;
  onAddLinhaManual: (ddd: string, numero: string) => void;
  /** Suspensão só é permitida em contratos Fastchip (salvo exceção temporária DH). */
  fastchip: boolean;
  /**
   * TEMPORÁRIO (pedido Eder — bater meta): cluster DH por entrega pode suspender sem Fastchip.
   * ER2 operacional legado não mantém esta excepção.
   */
  allowsSuspensionWithoutFastchip?: boolean;
  /**
   * Admin em edição: correção de linhas a suspender sem converter para Fastchip.
   * A tab não chama hasRole — o caller decide.
   */
  isAdminCorrectionWithoutFastchip?: boolean;
  /** Regra fim da suspensão — desabilita marcação de linhas para suspensão. */
  suspensaoBloqueadaContrato?: boolean;
  /** Novo contrato sob política 1056: oculta fluxo legado de suspensão nesta aba. */
  substituirPorOrientacao1056?: boolean;
  mensagemOrientacao1056?: string;
}

export const ContractFormLinhasDesativarTab: React.FC<ContractFormLinhasDesativarTabProps> = ({
  linhas,
  linhasParaDesativar,
  setLinhasParaDesativar,
  toggleLinhaDesativar,
  calcularDataDesativacao,
  teamContractRules,
  calcularAditivosNecessarios,
  onAddLinhaManual,
  fastchip,
  allowsSuspensionWithoutFastchip = false,
  isAdminCorrectionWithoutFastchip = false,
  suspensaoBloqueadaContrato = false,
  substituirPorOrientacao1056 = false,
  mensagemOrientacao1056,
}) => {
  const [manualDdd, setManualDdd] = useState('');
  const [manualNumero, setManualNumero] = useState('');

  const linhasRenegociacao = linhas.filter((linha) => linha.negociacao === 'Renegociação');

  const handleAddLinhaManual = () => {
    const ddd = manualDdd.replace(/\D/g, '').slice(0, 2);
    let numero = manualNumero.replace(/\D/g, '');
    if (ddd && numero.length > 9) numero = numero.slice(0, 9);
    if (!ddd || !numero) {
      alert('Informe o DDD e o número da linha a desativar.');
      return;
    }
    onAddLinhaManual(ddd, numero);
    setManualDdd('');
    setManualNumero('');
  };

  if (substituirPorOrientacao1056) {
    return (
      <div style={{ padding: '20px' }} data-testid="linhas-desativar-apenas-1056">
        <Header as="h3" style={{ color: '#2c3e50', marginBottom: '20px' }}>
          <Icon name="ban" />
          Linhas a Desativar
        </Header>
        <SuspensionBlockBanner message={mensagemOrientacao1056} />
        <Message info style={{ marginTop: 16 }}>
          <Message.Header>
            <Icon name="phone volume" />
            Cancelamento pelo cliente (TIM 1056)
          </Message.Header>
          <p style={{ marginBottom: 8 }}>
            Contratos novos não usam mais o fluxo de <strong>suspensão de linhas</strong>. Para linhas que o cliente
            deve cancelar diretamente na operadora, registre em{' '}
            <strong>Linhas para cancelamento pelo cliente</strong> após salvar o contrato (nesta aba ou no painel do
            contrato).
          </p>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {CLIENT_CANCELLATION_TIM_1056_SCRIPT.split('\n').map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Message>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Header as="h3" style={{ color: '#2c3e50', marginBottom: '20px' }}>
        <Icon name="ban" />
        Linhas a Desativar
      </Header>

      {suspensaoBloqueadaContrato && <SuspensionBlockBanner message={mensagemOrientacao1056} />}

      <Message warning>
        <Message.Header>
          <Icon name="info circle" />
          Linhas que serão canceladas após 120 dias
        </Message.Header>
        <p>
          Selecione as linhas de Renegociação que o <strong>cliente solicitou cancelar</strong>. Estas linhas serão
          <strong> automaticamente desativadas em 120 dias</strong> a partir da criação do contrato.
        </p>
        <p style={{ marginTop: '8px' }}>
          <strong>Importante:</strong> Essas linhas <strong>NÃO aparecem no PDF do contrato</strong> enviado ao cliente
          e <strong>NÃO são contabilizadas</strong> no valor total mensal.
        </p>
        <p style={{ marginTop: '8px' }}>Você também pode adicionar linhas manualmente informando apenas o DDD e o número.</p>
      </Message>

      {!fastchip && allowsSuspensionWithoutFastchip && (
        <Message positive>
          <Message.Header>
            <Icon name="info circle" />
            Suspensão liberada neste cluster (por entrega)
          </Message.Header>
          <p>
            No cluster <strong>DH - RJ/MG/ES</strong> você pode marcar linhas para suspensão mantendo o contrato{' '}
            <strong>por entrega</strong> — sem converter para Fastchip.
          </p>
        </Message>
      )}

      {!fastchip && isAdminCorrectionWithoutFastchip && (
        <Message info>
          <Message.Header>
            <Icon name="info circle" />
            Correção sem converter
          </Message.Header>
          <p>
            Nesta correção administrativa você pode marcar linhas para suspensão mantendo o contrato{' '}
            <strong>por entrega</strong> — sem converter para Fastchip.
          </p>
        </Message>
      )}

      {!fastchip && !allowsSuspensionWithoutFastchip && !isAdminCorrectionWithoutFastchip && (
        <Message info>
          <Message.Header>
            <Icon name="info circle" />
            Contrato sem Fastchip
          </Message.Header>
          <p>
            Ao marcar linhas para suspensão, será exibido um modal para <strong>converter o contrato para Fastchip</strong>.
            Sem estoque na região, a marcação fica bloqueada.
          </p>
        </Message>
      )}

      {linhasRenegociacao.length === 0 ? (
        <Message warning>
          <Icon name="warning" />
          <Message.Header>Nenhuma linha de Renegociação adicionada</Message.Header>
          <p>Adicione linhas de Renegociação na aba "Linhas" antes de selecionar linhas para desativar.</p>
        </Message>
      ) : (
        <Segment>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={1}>
                  <Checkbox
                    indeterminate={
                      linhasParaDesativar.size > 0 && linhasParaDesativar.size < linhasRenegociacao.length
                    }
                    checked={
                      linhasRenegociacao.length > 0 && linhasParaDesativar.size === linhasRenegociacao.length
                    }
                    onChange={() => {
                      if (suspensaoBloqueadaContrato) return;
                      if (linhasParaDesativar.size === linhasRenegociacao.length) {
                        setLinhasParaDesativar(new Set());
                      } else {
                        setLinhasParaDesativar(new Set(linhasRenegociacao.map((l) => l.id!)));
                      }
                    }}
                    disabled={suspensaoBloqueadaContrato}
                  />
                </Table.HeaderCell>
                <Table.HeaderCell>Negociação</Table.HeaderCell>
                <Table.HeaderCell>DDD</Table.HeaderCell>
                <Table.HeaderCell>Número</Table.HeaderCell>
                <Table.HeaderCell>Plano</Table.HeaderCell>
                <Table.HeaderCell>Valor Mensal</Table.HeaderCell>
                <Table.HeaderCell>Data Cancelamento</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {linhasRenegociacao.map((linha) => {
                const estaSelecionada = linhasParaDesativar.has(linha.id!);
                const dataDesativacao = estaSelecionada ? calcularDataDesativacao() : null;
                return (
                  <Table.Row
                    key={linha.id}
                    positive={estaSelecionada}
                    style={{
                      color: estaSelecionada ? '#c62828' : 'inherit',
                      fontWeight: estaSelecionada ? 'bold' : 'normal',
                    }}
                  >
                    <Table.Cell>
                      <Checkbox
                        checked={estaSelecionada}
                        disabled={suspensaoBloqueadaContrato}
                        onChange={() => {
                          if (suspensaoBloqueadaContrato) return;
                          toggleLinhaDesativar(linha.id!);
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell style={{ color: estaSelecionada ? '#c62828' : 'inherit' }}>
                      {linha.negociacao || '-'}
                    </Table.Cell>
                    <Table.Cell style={{ color: estaSelecionada ? '#c62828' : 'inherit' }}>
                      {linha.ddd || '-'}
                    </Table.Cell>
                    <Table.Cell style={{ color: estaSelecionada ? '#c62828' : 'inherit' }}>
                      {linha.numero
                        ? `${linha.ddd || ''}${linha.numero}`.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
                        : '-'}
                    </Table.Cell>
                    <Table.Cell style={{ color: estaSelecionada ? '#c62828' : 'inherit' }}>
                      {linha.planName || '-'}
                    </Table.Cell>
                    <Table.Cell style={{ color: estaSelecionada ? '#c62828' : 'inherit' }}>
                      {linha.valorMensal
                        ? `R$ ${parseFloat(linha.valorMensal.toString()).toFixed(2).replace('.', ',')}`
                        : '-'}
                    </Table.Cell>
                    <Table.Cell style={{ color: estaSelecionada ? '#c62828' : 'inherit' }}>
                      {dataDesativacao ? new Date(dataDesativacao).toLocaleDateString('pt-BR') : '-'}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>

          {teamContractRules.requireAditivosForSuspendedLines && linhasParaDesativar.size > 0 &&
            (() => {
              const linhasSuspensas = linhasParaDesativar.size;
              const aditivosNecessarios = calcularAditivosNecessarios(linhasSuspensas);
              const linhasAditivoAtuais = linhas.filter((l) => l.negociacao === 'Aditivo');
              const aditivosAtuais = linhasAditivoAtuais.length;
              const aditivosFaltando = Math.max(0, aditivosNecessarios - aditivosAtuais);
              return (
                <>
                  <Message success style={{ marginTop: '20px' }}>
                    <Icon name="check circle" />
                    <Message.Header>
                      {linhasParaDesativar.size}{' '}
                      {linhasParaDesativar.size === 1
                        ? 'linha selecionada para cancelamento'
                        : 'linhas selecionadas para cancelamento'}
                    </Message.Header>
                    <p>
                      <strong>Estas linhas serão canceladas pela equipe de suspensões</strong>.
                    </p>
                    <p style={{ marginTop: '8px', fontSize: '0.95em' }}>
                      ⚠️ Lembre-se: Essas linhas <strong>não aparecerão no PDF</strong> do contrato e{' '}
                      <strong>não serão contabilizadas</strong> no valor mensal.
                    </p>
                  </Message>

                  <Message
                    info
                    style={{
                      marginTop: '16px',
                      backgroundColor: aditivosFaltando > 0 ? '#ffebee' : '#e3f2fd',
                      border: `2px solid ${aditivosFaltando > 0 ? '#f44336' : '#2196f3'}`,
                      borderRadius: '8px',
                    }}
                  >
                    <Message.Header>
                      <Icon name={aditivosFaltando > 0 ? 'exclamation triangle' : 'info circle'} />
                      Regra de Aditivos
                    </Message.Header>
                    <p style={{ margin: '8px 0', fontWeight: 'bold' }}>
                      {linhasSuspensas} {linhasSuspensas === 1 ? 'linha suspensa requer' : 'linhas suspensas requerem'}{' '}
                      {aditivosNecessarios} {aditivosNecessarios === 1 ? 'aditivo' : 'aditivos'}
                    </p>
                    {aditivosFaltando > 0 ? (
                      <p style={{ margin: '8px 0', color: '#c62828', fontWeight: 'bold' }}>
                        ⚠️ Faltam {aditivosFaltando} {aditivosFaltando === 1 ? 'aditivo' : 'aditivos'}.{' '}
                        {aditivosFaltando === 1 ? 'Adicione' : 'Adicione'} mais{' '}
                        {aditivosFaltando === 1 ? 'linha aditiva' : 'linhas aditivas'} na aba "Linhas" antes de salvar o
                        contrato.
                      </p>
                    ) : (
                      <p style={{ margin: '8px 0', color: '#2e7d32', fontWeight: 'bold' }}>
                        ✅ Regra atendida: {aditivosAtuais}{' '}
                        {aditivosAtuais === 1 ? 'aditivo encontrado' : 'aditivos encontrados'}
                      </p>
                    )}
                  </Message>
                </>
              );
            })()}
        </Segment>
      )}

      <Segment style={{ marginTop: '20px' }}>
        <Header as="h4">
          <Icon name="plus circle" />
          <Header.Content>
            Adicionar linha manualmente para cancelamento
            <Header.Subheader>
              Use esta opção quando o cliente solicitar o <strong>cancelamento de outras linhas</strong> que não estão
              sendo renegociadas. Essas linhas serão desativadas em 120 dias.
            </Header.Subheader>
          </Header.Content>
        </Header>

        <Form>
          <Form.Group widths="equal">
            <Form.Input
              label="DDD"
              placeholder="Ex: 11"
              maxLength={2}
              value={manualDdd}
              onChange={(_, { value }) => setManualDdd((value || '').replace(/\D/g, '').slice(0, 2))}
            />
            <Form.Input
              label="Número"
              placeholder="Ex: 9XXXXXXXX"
              maxLength={9}
              value={manualNumero}
              onChange={(_, { value }) =>
                setManualNumero((value || '').replace(/\D/g, '').slice(0, 9))
              }
            />
          </Form.Group>

          <p style={{ color: '#666', fontSize: '0.9em' }}>
            <strong>Importante:</strong> As linhas adicionadas manualmente serão <strong>canceladas em 120 dias</strong>,
            não aparecerão no PDF do contrato e não impactam o valor mensal.
          </p>

          <Button
            type="button"
            icon
            labelPosition="left"
            color="blue"
            disabled={suspensaoBloqueadaContrato}
            onClick={handleAddLinhaManual}
          >
            <Icon name="plus" />
            Adicionar linha manual
          </Button>
        </Form>
      </Segment>
    </div>
  );
};
